<template>
  <div>
    <h2 class="text-[18px] text-[#333] mb-[16px] font-bold">
      {{ title }}
    </h2>
    <p class="text-[16px] text-[#333] mb-[40px]">
      {{ description }}
    </p>
    <div class="grid grid-cols-2 grid-flow-row gap-x-4 gap-y-1">
      <div>
        <p class="mb-0 text-[#333] text-center text-[14px]">Example</p>
      </div>
      <div>
        <p class="mb-0 text-[#333] text-center text-[14px]">Your photo</p>
      </div>
      <div class="rounded-md relative overflow-hidden aspect-[2/3]">
        <img class="h-full" :src="exampleImageSrc" alt="A birds eye view of someone's head, including the the crown" />
        <img
          aria-hidden="true"
          alt="Tick icon indicating the example is the correct way to take the photo"
          class="h-[24px] w-[24px] absolute top-[8px] right-[8px]"
          src="https://www.medexpress.co.uk/images/medexpress-resources/svg/tick_icon.svg"
        />
      </div>
      <label
        v-if="!imagePreview"
        v-on:click="clickUploadPhoto"
        for="actual-upload"
        class="cursor-pointer bg-white border-[#28AAE2] border-2 rounded-md border-dashed flex justify-center items-center flex-col aspect-[2/3]"
      >
        <span class="bg-[#28AAE2] w-[80px] h-[80px] rounded-full mb-[8px] relative">
          <img
            alt="Upload photo icon"
            class="absolute top-[16px] left-[19px]"
            src="https://www.medexpress.co.uk/images/medexpress-resources/svg/file-upload.svg"
          />
        </span>
        <span class="text-[#28AAE2] text-[16px] font-bold">Tap to upload</span>
      </label>
      <div
        v-if="imagePreview"
        class="rounded-md relative overflow-hidden aspect-[2/3] bg-white flex justify-center items-center flex-col"
      >
        <img alt="Preview of your image" v-if="imagePreview" :src="imagePreview" />
        <label
          for="actual-upload"
          v-on:click="clickChangePhoto"
          class="bg-[#ED1A52] font-[700] p-[8px] text-white absolute bottom-[8px] right-[8px] rounded-md cursor-pointer"
          >Change</label
        >
      </div>
      <input
        id="actual-upload"
        type="file"
        class="hidden"
        accept="image/png, image/jpeg"
        @change="handleImageSelection"
      />
    </div>

    <div
      v-if="message"
      class="relative mt-[26px] text-[#ED1A52] font-normal text-[16px] pl-[25px] before:left-0 before:absolute before:w-[20px] before:h-[20px] before:bg-error-exclamation"
    >
      {{ message }}
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import _ from "lodash";
import { amplitudeEvent } from "@/MXPixel/MedexpressAmplitude";

import "../../../stylesheets/common/tailwind.css";

interface PhotoPickerData {
  imagePreview: string | ArrayBuffer | null;
  imageFile: File | null;
  message: string | null;
}

export default Vue.extend({
  name: "PhotoPicker",
  props: {
    title: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      required: true,
    },
    exampleImageSrc: {
      type: String,
      required: true,
    },
    errorMessage: {
      type: String,
      required: true,
    },
    imagePreviewInit: {
      type: String,
      required: false,
    },
    imageFileInit: {
      type: File,
      required: false,
    },
  },
  data(): PhotoPickerData {
    return {
      imagePreview: this.imagePreviewInit,
      imageFile: this.imageFileInit,
      message: this.errorMessage,
    };
  },
  methods: {
    handleImageSelection(event: Event): void {
      const target = event?.target as HTMLInputElement;
      const files: FileList | null = target.files;

      if (_.isNull(files) || _.isEmpty(files)) {
        return;
      }

      this.imageFile = files[0];
      this.$emit("file-state-change", this.imageFile);

      this.loadImagePreview(files[0]);
    },
    loadImagePreview(file: File): void {
      const fileSizeMB = file.size / 1000000;

      const reader = new FileReader();
      reader.onload = (event: ProgressEvent<FileReader>) => {
        const result = event.target?.result as string;

        const mimeType = result.substring(result.indexOf(":") + 1, result.indexOf(";"));
        if (mimeType !== "image/jpeg" && mimeType !== "image/png") {
          this.resetImageStateWithError("The photo selected must be a JPG or PNG");
          return;
        } else if (fileSizeMB > 25) {
          this.resetImageStateWithError("The photo selected must be smaller than 25MB");
          return;
        } else {
          this.message = null;
        }

        amplitudeEvent("Photo Uploaded on Photo Upload");

        this.imagePreview = result ?? null;
        this.$emit("preview-state-change", this.imagePreview);
      };

      reader.readAsDataURL(file);
    },
    resetImageStateWithError(message: string): void {
      this.message = message;
      this.imagePreview = null;
      this.imageFile = null;
      this.$emit("file-state-change", this.imageFile);
      this.$emit("preview-state-change", this.imagePreview);
      this.$emit("failure", message);
    },
    clickUploadPhoto(): void {
      this.$emit("click-upload");
    },
    clickChangePhoto(): void {
      this.$emit("click-change");
    },
  },
  watch: {
    errorMessage(value) {
      this.message = value;
    },
  },
});
</script>
